/**
 * Pure view-model logic for the weekly proposal screen.
 * No React, no tRPC — testable with Vitest directly.
 */

import type { RouterOutputs } from '../../trpc'
import { deriveOrderStatus } from '@retail/orders-domain'
import type { OrderStatus } from '@retail/orders-domain'

// Aliases from RouterOutputs for brevity
export type MenuData = RouterOutputs['catalog']['store']['currentMenu']
export type ProductData = RouterOutputs['catalog']['store']['eligibleProducts'][number]
export type CategoryData = RouterOutputs['catalog']['categories']['list'][number]
export type OrderData = RouterOutputs['orders']['proposalForWeek']

export type StoreRole = 'VIEWER' | 'EDITOR'

/** Shown when the category list has not arrived yet, or names no such id. */
const UNKNOWN_CATEGORY = '—'

export type ProposalRow = {
  product: ProductData
  quantity: number
  /** Resolved here rather than in the table, which is a presentational leaf. */
  categoryName: string
}

export type ProposalViewModel = {
  rows: Array<ProposalRow>
  order: OrderData
  status: OrderStatus | null
  cutoffAt: Date | null
  delivery: NonNullable<OrderData>['delivery'] | null
  role: StoreRole
  isLoading: boolean
  isError: boolean
  week: {isoYear: number; isoWeek: number}
  // Derived
  totalPlates: number
  totalRefs: number
  isLocked: boolean
  isEditable: boolean
}

export type MergeProposalInput = {
  menu: MenuData | undefined
  products: Array<ProductData> | undefined
  /**
   * Undefined while the category list is in flight. Rows still render — the
   * category cell reads "—" until it arrives — so this never gates the screen.
   */
  categories: Array<CategoryData> | undefined
  order: OrderData | undefined
  role: StoreRole
  isLoading: boolean
  isError: boolean
  week: {isoYear: number; isoWeek: number}
  /**
   * The store's per-line floor. Above zero it is the SMALLEST quantity this
   * store can carry, so it is what a row reads at — see the row policy below.
   */
  minQuantityPerProduct: number
  nowMs?: number // injected for testing; defaults to Date.now()
}

/**
 * Merges the four tRPC query results into a unified view-model.
 *
 * Row policy: show only products that are in the menu's productIds
 * (the orderable carta). This is more correct than showing all eligible
 * products because eligibleProducts may include extras not on this menu.
 * Products are shown in the order defined by menu.productIds.
 */
export const mergeProposalViewModel = ({
  menu,
  products,
  categories,
  order,
  role,
  isLoading,
  isError,
  week,
  minQuantityPerProduct,
  nowMs,
}: MergeProposalInput): ProposalViewModel => {
  const now = nowMs ?? Date.now()

  // Build productId→product lookup
  const productMap = new Map<number, ProductData>(
    (products ?? []).map((product) => [product.id, product]),
  )

  // categoryId is the category's numeric spine id — resolve its display name here.
  const categoryNameById = new Map<number, string>(
    (categories ?? []).map((category) => [category.id, category.name]),
  )

  // Filter to menu's carta, preserving menu order
  const menuIds = menu?.productIds ?? []
  const rows: Array<ProposalRow> = menuIds
    .map((pid) => productMap.get(pid))
    .filter((product): product is ProductData => product !== undefined)
    .map((product) => {
      const line = order?.products.find((orderLine) => orderLine.productId === product.id)
      return {
        product,
        // A row reads at the store's floor or above, because that is the range
        // this store can carry: below the floor "not ordering a product" does
        // not exist for it. So a carta product the order does not name (the
        // carta gained it after the proposal was generated) and a stored zero
        // (a staff correction, or history) both read at the floor — which is
        // exactly what buildLines will write for them. Screen and payload say
        // the same thing, and that is the whole point: the earlier model that
        // floored the READING while the payload carried nothing promised units
        // MRP never received.
        //
        // With a floor of 0 this is the identity and the stored zero shows as
        // the zero it is — that store's legitimate opt-out.
        //
        // ⚠️ Only when there IS an order. With no proposal the floor would
        // invent a whole carta of units nothing will ever write, and the
        // summary would count them: that is the exact model this replaced.
        quantity: order ? Math.max(minQuantityPerProduct, line?.quantity ?? 0) : 0,
        categoryName: categoryNameById.get(product.categoryId) ?? UNKNOWN_CATEGORY,
      }
    })

  const cutoffAt: Date | null = order?.cutoffAt ?? null
  // Status derived purely from the cutoff; null when there's no order.
  const status: OrderStatus | null = order ? deriveOrderStatus(order, new Date(now)) : null

  const totalPlates = rows.reduce((sum, row) => sum + row.quantity, 0)
  const totalRefs = rows.filter((row) => row.quantity > 0).length

  // Locked when there is no order or the cutoff has passed (status !== EDITABLE).
  const isLocked = status !== 'EDITABLE'

  // Editable only if: EDITOR role + status is EDITABLE (before cutoff).
  const isEditable = role === 'EDITOR' && status === 'EDITABLE'

  const delivery = order?.delivery ?? null

  return {
    rows,
    order: order ?? null,
    status,
    cutoffAt,
    delivery,
    role,
    isLoading,
    isError,
    week,
    totalPlates,
    totalRefs,
    isLocked,
    isEditable,
  }
}
